/**
 * A contradicted version is shown as withheld, not as missing.
 *
 * "We have no rule for this" and "we have a rule and our own checking says it is wrong" are
 * different answers, and only one of them is somebody's job today. The finding renders at the
 * point of the refusal, with what the research found, its citation and the run that raised it.
 *
 * Clearing takes a mandatory reason and says what it commits to. The two other routes out — edit
 * the version, or queue fresh research — sit beside it, because overruling the finding is only
 * one of three reasonable responses to it.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '~/ui/Button';
import { Field, Textarea } from '~/ui/Field';
import { ConfirmDialog } from '~/ui/Modal';
import { useClearContradiction } from '~/api/mutations';
import { useToast } from '~/hooks/useToast';
import { problemsOf } from '~/api/client';
import { fmtInstant } from '~/lib/format';
import { routes } from '~/lib/routes';
import type { Contradiction } from '~/api/schema';
import styles from './Matter.module.css';

export interface WithheldBlockProps {
  code: string;
  matter: string;
  lane: string;
  versionId: string | null;
  contradiction: Contradiction;
  /** The dates the screen is being read with, so Edit keeps them. */
  dateParams: Record<string, string | undefined>;
}

export function WithheldBlock({
  code,
  matter,
  lane,
  versionId,
  contradiction,
  dateParams,
}: WithheldBlockProps) {
  const [clearing, setClearing] = useState(false);

  return (
    <div className={styles.withheld} role="note">
      <p>
        <strong>This version is withheld.</strong> The engine&rsquo;s own adversarial pass believes
        it is wrong, so it is excluded from every calculation until someone clears it. No date is
        produced from it — that is the point, not a failure.
      </p>
      <p style={{ marginTop: 8 }}>{contradiction.detail}</p>
      {contradiction.source ? (
        <p style={{ marginTop: 6 }}>
          Read at{' '}
          <a
            href={contradiction.source}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.externalLink}
          >
            {contradiction.source}
          </a>
        </p>
      ) : null}
      {contradiction.raisedByRunId ? (
        <p style={{ marginTop: 6 }}>
          Raised by{' '}
          <Link to={routes.run(contradiction.raisedByRunId)}>
            run {contradiction.raisedByRunId}
          </Link>
          {contradiction.raisedAt ? ` on ${fmtInstant(contradiction.raisedAt)}` : ''}
        </p>
      ) : null}

      <div className={styles.withheldActions}>
        {versionId ? (
          <Button size="sm" onClick={() => setClearing(true)}>
            Review contradiction
          </Button>
        ) : null}
        <Link
          to={routes.ruleEditor(code, matter, lane, dateParams)}
          style={{ alignSelf: 'center', fontSize: 12 }}
        >
          Edit the version
        </Link>
        <Link to={routes.sources(code, matter)} style={{ alignSelf: 'center', fontSize: 12 }}>
          Queue fresh research
        </Link>
      </div>

      {clearing && versionId ? (
        <ClearContradictionDialog
          code={code}
          matter={matter}
          lane={lane}
          versionId={versionId}
          onClose={() => setClearing(false)}
        />
      ) : null}
    </div>
  );
}

function ClearContradictionDialog({
  code,
  matter,
  lane,
  versionId,
  onClose,
}: {
  code: string;
  matter: string;
  lane: string;
  versionId: string;
  onClose: () => void;
}) {
  const clear = useClearContradiction(code, matter);
  const toast = useToast();
  const [reason, setReason] = useState('');

  return (
    <ConfirmDialog
      title="Clear this contradiction?"
      subtitle="The finding is not erased. It stays in the version's history with your reason against it."
      confirmLabel="Clear it"
      cancelLabel="Leave it withheld"
      pending={clear.isPending}
      onCancel={onClose}
      onConfirm={() =>
        clear.mutate(
          { lane, versionId, reason },
          {
            onSuccess: () => {
              toast.show('ok', 'The version is back in use and your reason is recorded against it.');
              onClose();
            },
            onError: (error) => {
              for (const problem of problemsOf(error)) toast.show('error', problem);
            },
          },
        )
      }
      body={
        <>
          <p style={{ marginBottom: 12 }}>
            Clearing it commits to three things: the version returns to use and starts producing
            dates again, your reason is recorded against it, and the contradiction stays in the
            history so anyone reading this rule later can see that a human overruled it, on this
            date, for this reason.
          </p>
          <Field
            label="Why does the contradiction not stand?"
            hint="Recorded against the version. It is required."
          >
            <Textarea
              rows={4}
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
            />
          </Field>
        </>
      }
    />
  );
}
